import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRuler, 
  faChild, 
  faUsers, 
  faWeight, 
  faBolt, 
  faTruck,
  faCalendarAlt,
  faArrowLeft,
  faShoppingCart,
  faStar,
  faCheck,
  faInfoCircle,
  faClock,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../context/CartContext';
import { useLocation as useServiceLocation } from '../../context/LocationContext';
import PlaceholderImage from '../common/PlaceholderImage';
import QuantitySelector from '../common/QuantitySelector';
import { getProductById, getProducts } from '../../services/productService';
import { getCategoryById } from '../../services/categoryService';
import { validateBookingRequest, getUnavailableDates, validateDateRange, getMinimumBookingDate } from '../../services/availabilityService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker-custom.css';
import '../../styles/rental-detail.css';
import '../../styles/ordering-paused.css';

// Add styles for booking notice
const bookingNoticeStyle = `
  .booking-notice {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #856404;
  }
  
  .booking-notice small {
    font-size: 13px;
    margin: 0;
  }
  
  .booking-notice .fa-exclamation-triangle {
    color: #f39c12;
    font-size: 14px;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = bookingNoticeStyle;
  document.head.appendChild(styleElement);
}

const RentalDetailPage = () => {
  const { id } = useParams();
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [relatedRentals, setRelatedRentals] = useState([]);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [dateValidation, setDateValidation] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const { addToCart } = useCart();
  const {
    currentLocation,
    isOrderingPaused,
    getOrderingPauseMessage
  } = useServiceLocation();

  // The warehouse that would fulfill this unit. "Both"/unspecified units follow
  // the area the customer is currently shopping.
  const fulfillmentArea =
    rental?.location === 'Angola' || rental?.location === 'Elkhart'
      ? rental.location
      : currentLocation;
  const orderingPaused = isOrderingPaused(fulfillmentArea);

  // Fetch product details from Firebase
  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      try {
        // Get product details
        const productData = await getProductById(id);
        
        if (!productData) {
          setError('Product not found');
          setLoading(false);
          return;
        }
        
        // Format the product data for UI
        const formattedProduct = {
          id: productData.id,
          name: productData.name,
          price: `$${productData.price}/day`,
          priceNumeric: productData.price,
          stock: productData.stock || 1,
          description: productData.description,
          dimensions: productData.dimensions || '',
          ageRange: productData.ageRange || '',
          capacity: productData.capacity || '',
          images: productData.images || [],
          categoryId: productData.categoryId,
          popular: productData.popular || false,
          location: productData.location || 'Elkhart',
          details: {
            description: productData.description,
            features: productData.features || [],
            safety: [
              `Suitable for ${productData.ageRange || 'children'}`,
              `Maximum capacity: ${productData.capacity || 'varies'}`,
              'Adult supervision required at all times',
              'No shoes, sharp objects, or food inside',
              'Not to be used in rain or high winds'
            ]
          },
          reviews: productData.reviews || []
        };
        
        setRental(formattedProduct);
        
        // Get category information
        if (productData.categoryId) {
          const categoryData = await getCategoryById(productData.categoryId);
          setCategory(categoryData);
        }
        
        // Fetch related products (same category)
        const allProducts = await getProducts();
        const related = allProducts
          .filter(product => 
            product.id !== id && 
            product.categoryId === productData.categoryId
          )
          .slice(0, 3)
          .map(product => ({
            id: product.id,
            name: product.name,
            price: `$${product.price}/day`,
            image: product.images && product.images.length > 0 ? product.images[0].url : '',
            description: product.description
          }));
        
        setRelatedRentals(related);
        setError(null);
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError('Failed to load product details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProductDetails();
  }, [id]);

  // Load unavailable dates when rental is loaded
  useEffect(() => {
    const loadUnavailableDates = async () => {
      if (!rental?.id) return;
      
      try {
        setAvailabilityLoading(true);
        const unavailable = await getUnavailableDates(
          rental.id, 
          minBookingDate, 
          maxBookingDate
        );
        setUnavailableDates(unavailable);
        setAvailabilityError(null);
      } catch (error) {
        console.error('Error loading unavailable dates:', error);
        setAvailabilityError('Unable to load availability information');
      } finally {
        setAvailabilityLoading(false);
      }
    };

    loadUnavailableDates();
  }, [rental?.id]);

  // Calculate the minimum allowed booking date (24 hours from now)
  const minBookingDate = getMinimumBookingDate();
  
  // Calculate the maximum allowed booking date (12 months from today)
  const maxBookingDate = new Date();
  maxBookingDate.setFullYear(maxBookingDate.getFullYear() + 1);

  // Filter out unavailable dates in DatePicker - ENHANCED VERSION
  const isDateUnavailable = (date) => {
    // Convert to local date string to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    const isBlocked = unavailableDates.includes(dateString);
    
    // Debug logging to see what's happening
    if (dateString.includes('2025-06-20') || dateString.includes('2025-06-21')) {
      console.log(`🗓️ Date check: ${dateString} → ${isBlocked ? 'BLOCKED' : 'AVAILABLE'}`);
      console.log(`📋 All unavailable dates:`, unavailableDates);
    }
    
    return isBlocked;
  };
  
  // Available time slots for delivery (until 6:00 PM)
  const deliveryTimeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', 
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'
  ];

  // Available time slots for pickup (until 8:00 PM)
  const pickupTimeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', 
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
    '6:00 PM', '7:00 PM', '8:00 PM'
  ];
  
  // Format dates for display
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleStartDateChange = async (date) => {
    setSelectedStartDate(date);
    setDateValidation(null);
    
    // Validate the date if rental is available (use same date for start and end)
    if (date && rental?.id) {
      await validateRentalPeriod(date, date);
    }
  };

  const handleStartTimeChange = (e) => {
    setSelectedStartTime(e.target.value);
  };

  const handleEndTimeChange = (e) => {
    setSelectedEndTime(e.target.value);
  };

  const validateRentalPeriod = async (startDate, endDate) => {
    try {
      setAvailabilityLoading(true);
      
      // First validate the date range itself
      const rangeValidation = validateDateRange(startDate, endDate);
      if (!rangeValidation.valid) {
        setAvailabilityError(rangeValidation.message);
        return;
      }
      
      // Then check availability
      const validation = await validateBookingRequest(rental.id, startDate, endDate);
      setDateValidation(validation);
      
      if (!validation.valid) {
        setAvailabilityError(validation.message);
      } else {
        setAvailabilityError(null);
      }
    } catch (error) {
      console.error('Error validating rental period:', error);
      setAvailabilityError('Unable to verify availability for this period');
    } finally {
      setAvailabilityLoading(false);
    }
  };
  
  const handleAddToCart = async () => {
    // Ordering for this unit's warehouse is paused from the admin dashboard.
    if (orderingPaused) {
      alert(getOrderingPauseMessage(fulfillmentArea));
      return;
    }

    if (!selectedStartDate) {
      alert('Please select an event date for your rental');
      return;
    }

    if (!selectedStartTime) {
      alert('Please select a delivery time');
      return;
    }

    if (!selectedEndTime) {
      alert('Please select a pickup time');
      return;
    }

    // Double-check availability before adding to cart
    if (!rental?.id) {
      alert('Product information is not available');
      return;
    }

    try {
      setAvailabilityLoading(true);
      
      // Validate quantity doesn't exceed stock
      if (selectedQuantity > rental.stock) {
        alert(`Sorry, only ${rental.stock} ${rental.name.toLowerCase()} available in stock.`);
        return;
      }
      
      // Use the same date for both start and end
      const eventDate = selectedStartDate;
      
      // Validate date range
      const rangeValidation = validateDateRange(eventDate, eventDate);
      if (!rangeValidation.valid) {
        alert(rangeValidation.message);
        return;
      }
      
      // Check availability
      const validation = await validateBookingRequest(rental.id, eventDate, eventDate);
      
      if (!validation.valid) {
        alert(validation.message);
        return;
      }

      // Format the rental item properly before adding to cart
      const rentalItem = {
        id: rental.id,
        name: rental.name,
        price: rental.price,
        mainImage: rental.images && rental.images.length > 0 ? rental.images[0].url : '',
        category: category ? category.name : '',
        location: rental.location
      };
      
      // Create booking info with same date for start and end
      const bookingInfo = {
        startDate: eventDate,
        endDate: eventDate,
        startTime: selectedStartTime,
        endTime: selectedEndTime
      };
      
      // Send the booking info to the cart with selected quantity
      addToCart(rentalItem, bookingInfo, selectedQuantity);
      setAddedToCart(true);
      
      // Reset added to cart message after 3 seconds
      setTimeout(() => {
        setAddedToCart(false);
      }, 3000);

    } catch (error) {
      console.error('Error validating booking:', error);
      alert('Unable to verify availability. Please try again.');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleImageSelect = (index) => {
    setSelectedImage(index);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading rental information...</p>
      </div>
    );
  }

  if (!rental) {
    return (
      <div className="rental-not-found">
        <h2>Rental Not Found</h2>
        <p>Sorry, we couldn't find the rental you're looking for.</p>
        <Link to="/rentals" className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Rentals
        </Link>
      </div>
    );
  }

  // Calculate average rating
  const averageRating = rental.reviews ? rental.reviews.reduce((acc, review) => acc + review.rating, 0) / rental.reviews.length : 0;

  return (
    <div className="rental-detail-page">
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Home</Link> &gt; 
          <Link to="/rentals">Rentals</Link> &gt; 
          <span>{rental.name}</span>
        </div>
      </div>
      
      <div className="rental-detail-container container">
        <div className="back-link-mobile">
          <Link to="/rentals" className="back-button">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Rentals
          </Link>
        </div>
        
        <div className="rental-detail-grid">
          <div className="rental-images">
            <div className="main-image">
              {rental.images && rental.images.length > 0 ? (
                <img src={rental.images[selectedImage].url} alt={rental.name} />
              ) : (
                <PlaceholderImage alt={rental.name} />
              )}
            </div>
            <div className="thumbnail-images">
              {rental.images.map((image, index) => (
                <div 
                  key={index} 
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={image.url} alt={`${rental.name} view ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>
          
          <div className="rental-info">
            <h1>{rental.name}</h1>
            
            <div className="rental-meta">
              <div className="rental-price">{rental.price}</div>
              <div className="rental-rating">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon 
                    key={i} 
                    icon={faStar} 
                    className={i < Math.round(averageRating) ? 'filled' : ''} 
                  />
                ))}
                <span>({rental.reviews ? rental.reviews.length : 0} reviews)</span>
              </div>
            </div>
            
            <div className="rental-quick-info">
              <div className="info-item">
                <FontAwesomeIcon icon={faRuler} />
                <span>{rental.dimensions}</span>
              </div>
              <div className="info-item">
                <FontAwesomeIcon icon={faChild} />
                <span>{rental.ageRange}</span>
              </div>
              <div className="info-item">
                <FontAwesomeIcon icon={faUsers} />
                <span>{rental.capacity}</span>
              </div>
            </div>
            
            <p className="rental-description">{rental.details.description}</p>
            
            <div className="rental-booking">
              <h3>Event Details</h3>
              
              <div className="booking-notice">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <small>Please note: All bookings must be made at least 24 hours in advance.</small>
              </div>
              
              <div className="date-range-selection">
                <div className="date-selection">
                  <label htmlFor="start-date">
                    <FontAwesomeIcon icon={faCalendarAlt} /> Event Date:
                  </label>
                  <div className="datepicker-container">
                    <DatePicker
                      id="start-date"
                      selected={selectedStartDate}
                      onChange={handleStartDateChange}
                      minDate={minBookingDate}
                      maxDate={maxBookingDate}
                      placeholderText="Select event date"
                      dateFormat="MMMM d, yyyy"
                      className="custom-datepicker"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      disabled={availabilityLoading}
                      filterDate={(date) => !isDateUnavailable(date)}
                    />
                  </div>
                  {selectedStartDate && (
                    <div className="selected-date-confirmation">
                      <FontAwesomeIcon icon={faCheck} /> Event Date: {formatDateForDisplay(selectedStartDate)}
                    </div>
                  )}
                </div>
              </div>

              {availabilityLoading && (
                <div className="availability-loading">
                  <small>Checking availability...</small>
                </div>
              )}
              
              {availabilityError && (
                <div className="availability-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> {availabilityError}
                </div>
              )}
              
              {selectedStartDate && !availabilityError && dateValidation?.valid && (
                <div className="selected-date-confirmation available">
                  <FontAwesomeIcon icon={faCheck} /> Date Available!
                </div>
              )}
              
              {selectedStartDate && dateValidation && !dateValidation.valid && (
                <div className="selected-date-confirmation unavailable">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> Date Not Available
                </div>
              )}

              <div className="time-range-selection">
                <div className="time-selection">
                  <label htmlFor="start-time">
                    <FontAwesomeIcon icon={faClock} /> Delivery Time:
                  </label>
                  <div className="time-select-container">
                    <select 
                      id="start-time" 
                      className="time-select" 
                      value={selectedStartTime} 
                      onChange={handleStartTimeChange}
                      required
                      disabled={!selectedStartDate || availabilityLoading || (dateValidation && !dateValidation.valid)}
                    >
                      <option value="">Select delivery time</option>
                      {deliveryTimeSlots.map((time, index) => (
                        <option key={index} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                  {selectedStartTime && selectedStartDate && dateValidation?.valid && (
                    <div className="selected-time-confirmation">
                      <FontAwesomeIcon icon={faCheck} /> Delivery: {selectedStartTime}
                    </div>
                  )}
                </div>

                <div className="time-selection">
                  <label htmlFor="end-time">
                    <FontAwesomeIcon icon={faClock} /> Pickup Time:
                  </label>
                  <div className="time-select-container">
                    <select 
                      id="end-time" 
                      className="time-select" 
                      value={selectedEndTime} 
                      onChange={handleEndTimeChange}
                      required
                      disabled={!selectedStartDate || availabilityLoading || (dateValidation && !dateValidation.valid)}
                    >
                      <option value="">Select pickup time</option>
                      {pickupTimeSlots.map((time, index) => (
                        <option key={index} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                  {selectedEndTime && selectedStartDate && dateValidation?.valid && (
                    <div className="selected-time-confirmation">
                      <FontAwesomeIcon icon={faCheck} /> Pickup: {selectedEndTime}
                    </div>
                  )}
                </div>
              </div>

              <div className="date-helper-text">
                <small>
                  <FontAwesomeIcon icon={faInfoCircle} /> We setup at your delivery time, then return to pickup and teardown at your pickup time
                </small>
              </div>
              
              {/* Show quantity selector for Tables and Chairs only */}
              {category && (category.name === 'Tables' || category.name === 'Chairs') && (
                <QuantitySelector
                  quantity={selectedQuantity}
                  onQuantityChange={setSelectedQuantity}
                  stock={rental.stock}
                  productName={rental.name}
                  categoryName={category.name}
                  pricePerItem={rental.priceNumeric}
                />
              )}
              
              {orderingPaused && (
                <div className="ordering-paused-banner" role="status">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <div>
                    <strong>
                      Online ordering is paused for our {fulfillmentArea} location.
                    </strong>
                    <span>{getOrderingPauseMessage(fulfillmentArea)}</span>
                  </div>
                </div>
              )}

              <button
                className={`add-to-cart-button ${orderingPaused ? 'ordering-disabled' : ''}`}
                onClick={handleAddToCart}
                disabled={orderingPaused || addedToCart || !selectedStartDate || !selectedStartTime || !selectedEndTime || availabilityLoading || (dateValidation && !dateValidation.valid)}
              >
                {orderingPaused ? (
                  <span>
                    <FontAwesomeIcon icon={faExclamationTriangle} /> Ordering Paused
                  </span>
                ) : availabilityLoading ? (
                  <span>
                    Checking Availability...
                  </span>
                ) : addedToCart ? (
                  <span>
                    <FontAwesomeIcon icon={faCheck} /> Added to Cart
                  </span>
                ) : (
                  <span>
                    <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
                  </span>
                )}
              </button>
              
              {unavailableDates.length > 0 && (
                <div className="availability-info">
                  <p><FontAwesomeIcon icon={faInfoCircle} /> Some dates are unavailable due to existing bookings. Unavailable dates are excluded from the calendar.</p>
                </div>
              )}
            </div>
            
            <div className="rental-note">
              <FontAwesomeIcon icon={faInfoCircle} />
              <p>Price includes setup and takedown. Delivery is calculated at checkout within a 25 mile radius.</p>
            </div>
          </div>
        </div>
        
        <div className="rental-tabs">
          <div className="tab-buttons">
            <button 
              className={activeTab === 'description' ? 'active' : ''} 
              onClick={() => handleTabChange('description')}
            >
              Features & Specs
            </button>
            <button 
              className={activeTab === 'safety' ? 'active' : ''} 
              onClick={() => handleTabChange('safety')}
            >
              Safety Information
            </button>
            <button 
              className={activeTab === 'reviews' ? 'active' : ''} 
              onClick={() => handleTabChange('reviews')}
            >
              Reviews ({rental.reviews ? rental.reviews.length : 0})
            </button>
          </div>
          
          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="tab-description">
                <div className="features-list">
                  <h3>Features</h3>
                  <ul>
                    {rental.details.features.map((feature, index) => (
                      <li key={index}>
                        <FontAwesomeIcon icon={faCheck} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="specs-list">
                  <h3>Specifications</h3>
                  <div className="specs-grid">
                    {/* Add specs array to rental items */}
                    {/* {rental.specs.map((spec, index) => (
                      <div key={index} className="spec-item">
                        <FontAwesomeIcon icon={spec.icon} />
                        <span>{spec.text}</span>
                      </div>
                    ))} */}
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'safety' && (
              <div className="tab-safety">
                <h3>Safety Guidelines</h3>
                <p>For the safety of all participants, please follow these important guidelines:</p>
                <ul className="safety-list">
                  {rental.details.safety.map((item, index) => (
                    <li key={index}>
                      <FontAwesomeIcon icon={faCheck} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {activeTab === 'reviews' && (
              <div className="tab-reviews">
                <div className="reviews-summary">
                  <div className="average-rating">
                    <div className="rating-number">{averageRating.toFixed(1)}</div>
                    <div className="rating-stars">
                      {[...Array(5)].map((_, i) => (
                        <FontAwesomeIcon 
                          key={i} 
                          icon={faStar} 
                          className={i < Math.round(averageRating) ? 'filled' : ''} 
                        />
                      ))}
                      <span>({rental.reviews ? rental.reviews.length : 0} reviews)</span>
                    </div>
                  </div>
                </div>
                
                <div className="reviews-list">
                  {rental.reviews ? rental.reviews.map((review, index) => (
                    <div key={index} className="review-item">
                      <div className="review-header">
                        <div className="reviewer-name">{review.name}</div>
                        <div className="review-date">{new Date(review.date).toLocaleDateString()}</div>
                      </div>
                      <div className="review-rating">
                        {[...Array(5)].map((_, i) => (
                          <FontAwesomeIcon 
                            key={i} 
                            icon={faStar} 
                            className={i < review.rating ? 'filled' : ''} 
                          />
                        ))}
                      </div>
                      <div className="review-comment">{review.comment}</div>
                    </div>
                  )) : <p>No reviews yet.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="related-rentals">
          <h2>You May Also Like</h2>
          <div className="related-rentals-grid">
            {relatedRentals.map(item => (
              <div key={item.id} className="related-rental-item">
                <div className="related-rental-image">
                  <img src={item.image} alt={item.name} />
                </div>
                <h3>{item.name}</h3>
                <p className="related-rental-price">{item.price}</p>
                <Link to={`/rentals/${item.id}`} className="view-details-button">
                  View Details
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalDetailPage;
